import React, { useEffect, useState } from 'react';
import { Loader2Icon, LogOutIcon, RefreshCwIcon } from 'lucide-react';
import { ScheduleFields, emptySchedule, scheduleToEpoch, type ScheduleDraft } from './ScheduleFields';
import { formatSchedule } from '../utils/schedule';
import type { EventSummary } from '../types/session';
interface EventSetupProps {
  hostEmail: string;
  busy: boolean;
  error: string | null;
  listEvents: () => Promise<EventSummary[]>;
  onOpen: (code: string, name: string, chatEnabled: boolean, startsAt?: number | null, timeZone?: string | null) => void;
  onSignOut: () => void;
}
const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
const randomCode = () => Array.from({
  length: 6
}, () => ALPHABET[Math.floor(Math.random() * ALPHABET.length)]).join('');
export function EventSetup({
  hostEmail,
  busy,
  error,
  listEvents,
  onOpen,
  onSignOut
}: EventSetupProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState(randomCode);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [schedule, setSchedule] = useState<ScheduleDraft>(emptySchedule);
  const [events, setEvents] = useState<EventSummary[]>([]);
  const startsAt = scheduleToEpoch(schedule);
  useEffect(() => {
    let alive = true;
    listEvents().then(rows => {
      if (alive) setEvents(rows);
    });
    return () => {
      alive = false;
    };
  }, [listEvents]);
  return <main className="flex min-h-full w-full items-center justify-center bg-ink px-5 py-12 font-sans" data-id="element-170" data-mp="39t83axp38qji">
      <div className="w-full max-w-xl" data-id="element-171" data-mp="35so65u4ilmxd">
        <div className="flex items-start justify-between gap-4" data-id="element-172" data-mp="311ft8o3z7jfg">
          <div data-id="element-173" data-mp="30zlcllzqmdh6">
            <p className="text-[0.7rem] font-semibold uppercase tracking-[0.28em] text-gold" data-id="element-174" data-mp="30z36lxn9wkw2">
              Administrator
            </p>
            <h1 className="mt-3 font-display text-4xl leading-tight text-white sm:text-5xl" data-id="element-175" data-mp="3thyyqobpob0f">
              Set up your event
            </h1>
            <p className="mt-3 text-sm text-muted" data-id="element-176" data-mp="3oppe9ntd835j">
              Signed in as <span className="text-body" data-id="element-177" data-mp="biwc5c9ltalv">{hostEmail}</span>
            </p>
          </div>
          <button type="button" onClick={onSignOut} className="inline-flex shrink-0 items-center gap-2 rounded-full border border-line px-3.5 py-2 text-xs font-semibold text-body transition-colors duration-150 ease-emphasis hover:border-crimson hover:text-crimson focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-178" data-mp="gh7nxsjglv6q">
            <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-179" data-mp="3twnhogtmcmwl" />
            Sign out
          </button>
        </div>

        <form onSubmit={e => {
        e.preventDefault();
        if (!busy) onOpen(code, name, chatEnabled, startsAt, schedule.timeZone);
      }} className="mt-9 space-y-6" data-id="element-180" data-mp="3fxw6b2655fc5">
          <div data-id="element-181" data-mp="1e2vkcdfx0di9">
            <label htmlFor="event-name" className="text-sm font-semibold text-white" data-id="element-182" data-mp="1i61mp9zwpkjg">
              Event name
            </label>
            <input id="event-name" value={name} onChange={e => setName(e.target.value)} maxLength={60} disabled={busy} placeholder="Summer Kickoff Raffle" className="mt-2 w-full rounded-lg border border-line bg-raised px-3 py-3 text-sm text-white placeholder:text-muted/70 focus:border-gold focus:outline-none disabled:opacity-50" data-id="element-183" data-mp="32pdu68rfhtkt" />
          </div>

          <div data-id="element-184" data-mp="3hd8lbkpsfpvs">
            <label htmlFor="event-code" className="text-sm font-semibold text-white" data-id="element-185" data-mp="3hdrsq7htavvk">
              Invitation code
            </label>
            <p className="mt-1 text-xs text-muted" data-id="element-186" data-mp="3vbq3l2wxca6b">
              Participants type this to join. Share it however you like.
            </p>
            <div className="mt-2 flex gap-2" data-id="element-187" data-mp="20z0bmqq49b1k">
              <input id="event-code" value={code} onChange={e => setCode(e.target.value.toUpperCase().replace(/\s+/g, ''))} maxLength={12} disabled={busy} className="min-w-0 flex-1 rounded-lg border border-line bg-raised px-3 py-3 font-semibold tracking-[0.22em] text-white focus:border-gold focus:outline-none disabled:opacity-50" data-id="element-188" data-mp="2101ql6a96hok" />
              <button type="button" onClick={() => setCode(randomCode())} disabled={busy} className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-line px-3.5 text-xs font-semibold text-body transition-colors duration-150 ease-emphasis hover:border-gold hover:text-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold" data-id="element-189" data-mp="27dfpud32axl9">
                <RefreshCwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-190" data-mp="1tgsdb1ymlyu8" />
                New code
              </button>
            </div>
          </div>

          <div className="rounded-lg border border-line bg-surface p-4" data-id="element-191" data-mp="10yn5lvyfbeoa">
            <p className="text-sm font-semibold text-white" data-id="element-192" data-mp="tfnj3hfo170p">Scheduled start</p>
            <p className="mt-1 text-xs text-muted" data-id="element-193" data-mp="tdkvf41zjpz5">
              Optional. Until this time, participants see an animated countdown instead
              of the draw.
            </p>
            <div className="mt-3" data-id="element-194" data-mp="jfws3pqbda5d">
              <ScheduleFields idPrefix="setup" value={schedule} disabled={busy} onChange={setSchedule} data-id="element-195" data-mp="eieomev2fn4r" />
            </div>
            {startsAt !== null && <p className="mt-3 text-xs text-gold" data-id="element-196" data-mp="5p1n4oixrd72">
                Starts {formatSchedule(startsAt, schedule.timeZone)}
                {startsAt <= Date.now() ? ' — that time has already passed' : ''}
              </p>}
          </div>

          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-line bg-surface p-4" data-id="element-197" data-mp="3rvgf470e5wbz">
            <input type="checkbox" checked={chatEnabled} onChange={e => setChatEnabled(e.target.checked)} disabled={busy} className="mt-0.5 h-4 w-4 accent-[#E3B23C]" data-id="element-198" data-mp="3iwuzajmb0vew" />
            <span data-id="element-199" data-mp="3dc5irfmmnwe3">
              <span className="block text-sm font-semibold text-white" data-id="element-200" data-mp="3d8hqejsx5g0y">
                Chat and comments on
              </span>
              <span className="mt-1 block text-xs text-muted" data-id="element-201" data-mp="349mgoxqo1nsw">
                Participants can post live while the draw runs. You can pause or wipe it
                at any time.
              </span>
            </span>
          </label>

          {error && <p role="alert" className="text-sm text-crimson" data-id="element-202" data-mp="3w0am4lzdhfcd">
              {error}
            </p>}

          <button type="submit" disabled={busy || code.trim().length < 4} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-bold uppercase tracking-[0.14em] text-ink transition-[background-color,transform] duration-150 ease-emphasis hover:bg-[#F0C154] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-line disabled:text-muted disabled:active:scale-100" data-id="element-203" data-mp="3i7umlv5ojzg7">
            {busy && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-204" data-mp="12nilaytv22n1" />}
            Start the event
          </button>
        </form>

        {events.length > 0 && <div className="mt-10 border-t border-line pt-6" data-id="element-205" data-mp="a0ea2c46cs2t">
            <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-muted" data-id="element-206" data-mp="5yav3diar5cs">
              Reopen an event
            </h2>
            <ul className="mt-3 divide-y divide-line/70" data-id="element-207" data-mp="jyllkffp2hxf">
              {events.map(ev => <li key={ev.code} className="flex items-center justify-between gap-4 py-3" data-id="element-208" data-mp="exxy23qocj7y">
                  <div className="min-w-0" data-id="element-209" data-mp="1vbwixdh1ewy2">
                    <p className="truncate text-sm font-medium text-body" data-id="element-210" data-mp="1veseww9brbjv">{ev.name}</p>
                    <p className="text-xs tracking-[0.16em] text-muted" data-id="element-211" data-mp="1qfz0n1m7rgin">{ev.code}</p>
                  </div>
                  <button type="button" onClick={() => onOpen(ev.code, ev.name, ev.chatEnabled)} disabled={busy} className="shrink-0 text-xs font-semibold text-gold transition-colors duration-150 ease-emphasis hover:text-[#F0C154] focus-visible:outline focus-visible:outline-1 focus-visible:outline-gold disabled:text-muted" data-id="element-212" data-mp="3ks9vjexhxa21">
                    Open
                  </button>
                </li>)}
            </ul>
          </div>}
      </div>
    </main>;
}